(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function initReveal(scope = document) {
    const nodes = [...scope.querySelectorAll('.cw-reveal:not([data-cw-ready])')];
    if (!nodes.length) return;
    if (reducedMotion || !('IntersectionObserver' in window)) {
      nodes.forEach((el) => { el.dataset.cwReady = '1'; el.classList.add('is-visible'); });
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.14, rootMargin: '0px 0px -7% 0px' });
    nodes.forEach((el) => { el.dataset.cwReady = '1'; observer.observe(el); });
  }

  function initParallax(scope = document) {
    if (reducedMotion) return;
    const nodes = [...scope.querySelectorAll('[data-cw-parallax]:not([data-cw-parallax-ready])')];
    if (!nodes.length) return;
    nodes.forEach((el) => { el.dataset.cwParallaxReady = '1'; });
    let ticking = false;
    const update = () => {
      const vh = window.innerHeight || 1;
      nodes.forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.bottom < -200 || rect.top > vh + 200) return;
        const center = rect.top + rect.height / 2;
        const progress = (center - vh / 2) / vh;
        const amount = Number(el.dataset.cwParallax || 24);
        el.style.setProperty('--cw-parallax-y', `${progress * amount}px`);
      });
      ticking = false;
    };
    const onScroll = () => {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  function initStory(scope = document) {
    scope.querySelectorAll('[data-cw-story]:not([data-cw-story-ready])').forEach((story) => {
      story.dataset.cwStoryReady = '1';
      const steps = [...story.querySelectorAll('[data-cw-story-step]')];
      const panels = [...story.querySelectorAll('[data-cw-story-panel]')];
      const autoplaySeconds = Math.max(3, Number(story.dataset.cwStoryAutoplay || 5));
      if (!steps.length || !panels.length) return;

      let activeIndex = 0;
      let autoplayTimer = null;
      let sectionVisible = !('IntersectionObserver' in window);

      const setActive = (index) => {
        activeIndex = ((index % steps.length) + steps.length) % steps.length;
        steps.forEach((step, i) => {
          const active = i === activeIndex;
          step.classList.toggle('is-active', active);
          step.setAttribute('aria-pressed', String(active));
        });
        panels.forEach((panel, i) => panel.classList.toggle('is-active', i === activeIndex));
      };

      const stopAutoplay = () => {
        if (autoplayTimer) {
          window.clearInterval(autoplayTimer);
          autoplayTimer = null;
        }
      };

      const startAutoplay = () => {
        stopAutoplay();
        if (reducedMotion || !sectionVisible || steps.length < 2) return;
        autoplayTimer = window.setInterval(() => setActive(activeIndex + 1), autoplaySeconds * 1000);
      };

      const choose = (index) => {
        setActive(index);
        startAutoplay();
      };

      steps.forEach((step, index) => {
        step.addEventListener('click', () => choose(index));
        step.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            choose(index);
          }
        });
      });

      setActive(0);

      if ('IntersectionObserver' in window) {
        const visibilityObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            sectionVisible = entry.isIntersecting;
            if (sectionVisible) startAutoplay();
            else stopAutoplay();
          });
        }, { threshold: 0.2 });
        visibilityObserver.observe(story);
      }

      startAutoplay();
    });
  }

  function initMobileHeader(scope = document) {
    scope.querySelectorAll('[data-cw-header]:not([data-cw-header-ready])').forEach((wrap) => {
      wrap.dataset.cwHeaderReady = '1';
      const toggle = wrap.querySelector('[data-cw-menu-toggle]');
      if (!toggle) return;
      toggle.addEventListener('click', () => {
        const open = wrap.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', String(open));
      });
    });
  }

  function initAll(scope = document) {
    initReveal(scope);
    initParallax(scope);
    initStory(scope);
    initMobileHeader(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();

/* v1.1 functional UI */
(() => {
  const money = (cents, currency = 'USD') => {
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format((Number(cents) || 0) / 100);
    } catch (_) {
      return `${((Number(cents) || 0) / 100).toFixed(2)} ${currency}`;
    }
  };

  const escapeHTML = (value = '') => String(value).replace(/[&<>'"]/g, (char) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#039;', '"': '&quot;'
  }[char]));

  function initHeaderFunctions(scope = document) {
    scope.querySelectorAll(
      '[data-cw-header]:not([data-cw-functions-ready])'
    ).forEach((header) => {
      header.dataset.cwFunctionsReady = '1';

      const sectionRoot =
        header.closest('.shopify-section') ||
        document;

      const searchOpen =
        header.querySelector(
          '[data-cw-search-open]'
        );

      const searchModal =
        sectionRoot.querySelector(
          '[data-cw-search-modal]'
        );

      if (!searchOpen || !searchModal) return;

      const searchCloseButtons =
        searchModal.querySelectorAll(
          '[data-cw-search-close]'
        );

      const searchInput =
        searchModal.querySelector(
          '[data-cw-search-input]'
        );

      const searchForm =
        searchModal.querySelector(
          '.cw-search-modal__form'
        );

      const searchHeading =
        searchModal.querySelector(
          '[data-cw-search-heading]'
        );

      const searchDefault =
        searchModal.querySelector(
          '[data-cw-search-default]'
        );

      const searchResults =
        searchModal.querySelector(
          '[data-cw-search-results]'
        );

      const searchEmpty =
        searchModal.querySelector(
          '[data-cw-search-empty]'
        );

      const searchIndexNode =
        searchModal.querySelector(
          '[data-cw-search-index]'
        );

      const menuToggle =
        header.querySelector(
          '[data-cw-menu-toggle]'
        );

      let localIndex = [];

      try {
        localIndex = JSON.parse(
          searchIndexNode?.textContent || '[]'
        );
      } catch (_) {
        localIndex = [];
      }

      let searchTimer = null;
      let searchController = null;
      let searchSequence = 0;

      const normalize = (value = '') =>
        String(value)
          .toLowerCase()
          .normalize('NFKD')
          .replace(/[^\w\s-]/g, ' ')
          .replace(/[_-]+/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();

      const stripHTML = (value = '') => {
        const node =
          document.createElement('div');

        node.innerHTML =
          String(value || '');

        return (
          node.textContent ||
          node.innerText ||
          ''
        ).trim();
      };

      const textMatchesQuery = (
        text,
        query
      ) => {
        const tokens =
          getTokens(query);

        if (!tokens.length) {
          return true;
        }

        const words =
          normalize(text)
            .split(' ')
            .filter(Boolean);

        return tokens.every((token) => {
          if (token.length <= 2) {
            return words.includes(token);
          }

          return words.some(
            (word) =>
              word.includes(token)
          );
        });
      };

      const extractContentBlocks = (
        html,
        title,
        query
      ) => {
        const documentHTML =
          new DOMParser()
            .parseFromString(
              String(html || ''),
              'text/html'
            );

        const main =
          documentHTML.querySelector(
            'main#MainContent'
          ) ||
          documentHTML.querySelector(
            'main'
          ) ||
          documentHTML.querySelector(
            '[role="main"]'
          ) ||
          documentHTML.body;

        if (!main) {
          return [];
        }

        const titleMatches =
          textMatchesQuery(
            title,
            query
          );

        const elements = [
          ...main.querySelectorAll(
            'h1, h2, h3, h4, p, li'
          )
        ];

        const blocks = [];
        const seen = new Set();

        elements.forEach((element) => {
          const text =
            String(
              element.textContent || ''
            )
              .replace(/\s+/g, ' ')
              .trim();

          if (text.length < 20) {
            return;
          }

          const key =
            normalize(text);

          if (
            !key ||
            seen.has(key)
          ) {
            return;
          }

          if (
            !titleMatches &&
            !textMatchesQuery(
              text,
              query
            )
          ) {
            return;
          }

          seen.add(key);
          blocks.push(text);
        });

        return blocks.slice(0, 10);
      };

      const fetchRenderedContent =
        async (
          item,
          query,
          signal
        ) => {
          try {
            const target =
              new URL(
                item.url,
                window.location.origin
              );

            if (
              target.origin !==
              window.location.origin
            ) {
              return [];
            }

            const response =
              await fetch(
                target.href,
                {
                  headers: {
                    Accept: 'text/html'
                  },
                  signal
                }
              );

            if (!response.ok) {
              return [];
            }

            const html =
              await response.text();

            const blocks =
              extractContentBlocks(
                html,
                item.title,
                query
              );

            return blocks.map(
              (text) => ({
                title: item.title,
                type: 'Content',
                url: item.url,
                description: text,
                keywords: text,
                local: item.local,
                contentMatch: true
              })
            );
          } catch (error) {
            if (
              error.name !==
              'AbortError'
            ) {
              return [];
            }

            throw error;
          }
        };

      const getTokens = (query) =>
        normalize(query)
          .split(' ')
          .filter(Boolean);

      const matchesQuery = (
        item,
        query
      ) => {
        const tokens =
          getTokens(query);

        if (!tokens.length) return true;

        const haystack =
          normalize([
            item.title,
            item.description,
            item.keywords,
            item.type
          ].filter(Boolean).join(' '));

        const words =
          haystack
            .split(' ')
            .filter(Boolean);

        return tokens.every((token) => {
          if (token.length <= 2) {
            return words.includes(token);
          }

          return words.some(
            (word) =>
              word.includes(token)
          );
        });
      };

      const scoreResult = (
        item,
        query
      ) => {
        const q =
          normalize(query);

        const title =
          normalize(item.title);

        const words =
          title.split(' ');

        let score = 0;

        if (title === q) {
          score += 100;
        } else if (
          title.startsWith(q)
        ) {
          score += 70;
        } else if (
          title.includes(q)
        ) {
          score += 50;
        }

        getTokens(query).forEach(
          (token) => {
            if (
              words.includes(token)
            ) {
              score += 18;
            } else if (
              title.includes(token)
            ) {
              score += 10;
            }
          }
        );

        if (item.local) {
          score += 5;
        }

        if (item.contentMatch) {
          score -= 15;
        }

        return score;
      };

      const fetchShopifyResults =
        async (
          query,
          signal
        ) => {
          const params =
            new URLSearchParams({
              q: query,

              'resources[type]':
                'product,page,article,collection',

              'resources[limit]':
                '10',

              'resources[limit_scope]':
                'each',

              'resources[options][fields]':
                'title,body,author,tag,product_type,vendor',

              'resources[options][unavailable_products]':
                'last'
            });

          const response =
            await fetch(
              `/search/suggest.json?${params.toString()}`,
              {
                headers: {
                  Accept:
                    'application/json'
                },
                signal
              }
            );

          if (!response.ok) {
            throw new Error(
              'Search request failed'
            );
          }

          const data =
            await response.json();

          const resources =
            data?.resources
              ?.results || {};

          const output = [];

          const addItems = (
            items,
            type
          ) => {
            (items || []).forEach(
              (item) => {
                output.push({
                  title:
                    item.title || '',
                  url:
                    item.url || '#',
                  type,
                  description:
                    stripHTML(
                      item.body ||
                      item.description ||
                      item.vendor ||
                      ''
                    ),
                  keywords: '',
                  local: false
                });
              }
            );
          };

          addItems(
            resources.pages,
            'Page'
          );

          addItems(
            resources.articles,
            'Article'
          );

          addItems(
            resources.products,
            'Product'
          );

          addItems(
            resources.collections,
            'Collection'
          );

          return output;
        };

      const renderDefault = () => {
        if (searchHeading) {
          searchHeading.textContent =
            'Snabbb Apps';
        }

        if (searchDefault) {
          searchDefault.hidden = false;
        }

        if (searchResults) {
          searchResults.hidden = true;
          searchResults.innerHTML = '';
        }

        if (searchEmpty) {
          searchEmpty.hidden = true;
        }
      };

      const renderResults = (
        items,
        query
      ) => {
        if (!searchHeading) return;

        searchHeading.textContent =
          `Search results for “${query}”`;

        if (searchDefault) {
          searchDefault.hidden = true;
        }

        if (!items.length) {
          if (searchResults) {
            searchResults.hidden = true;
            searchResults.innerHTML = '';
          }

          if (searchEmpty) {
            searchEmpty.hidden = false;
          }

          return;
        }

        if (searchEmpty) {
          searchEmpty.hidden = true;
        }

        if (!searchResults) return;

        searchResults.hidden = false;

        searchResults.innerHTML =
          items.map((item) => {
            const type =
              escapeHTML(
                item.type || 'Result'
              );

            const title =
              escapeHTML(
                item.title || ''
              );

            const description =
              escapeHTML(
                item.description || ''
              );

            const url =
              escapeHTML(
                item.url || '#'
              );

            const initial =
              escapeHTML(
                String(
                  item.type || 'R'
                ).charAt(0)
              );

            return `
              <a
                class="cw-search-live__item"
                href="${url}"
              >
                <span
                  class="cw-search-live__icon"
                  aria-hidden="true"
                >
                  ${initial}
                </span>

                <span
                  class="cw-search-live__copy"
                >
                  <span
                    class="cw-search-live__type"
                  >
                    ${type}
                  </span>

                  <strong>
                    ${title}
                  </strong>

                  ${
                    description
                      ? `
                        <small>
                          ${description}
                        </small>
                      `
                      : ''
                  }
                </span>

                <span
                  class="cw-search-live__arrow"
                  aria-hidden="true"
                >
                  →
                </span>
              </a>
            `;
          }).join('');
      };

      const runSearch =
        async (rawQuery) => {
          const query =
            String(
              rawQuery || ''
            ).trim();

          if (!query) {
            searchController
              ?.abort();

            renderDefault();
            return;
          }

          const sequence =
            ++searchSequence;

          searchController
            ?.abort();

          searchController =
            new AbortController();

          if (searchHeading) {
            searchHeading.textContent =
              'Searching…';
          }

          if (searchDefault) {
            searchDefault.hidden = true;
          }

          if (searchEmpty) {
            searchEmpty.hidden = true;
          }

          if (searchResults) {
            searchResults.hidden =
              false;

            searchResults.innerHTML =
              '<div class="cw-search-live__loading">Searching…</div>';
          }

          const localResults =
            localIndex
              .map((item) => ({
                ...item,
                local: true
              }))
              .filter((item) =>
                matchesQuery(
                  item,
                  query
                )
              );

          let remoteResults = [];

          try {
            remoteResults =
              await fetchShopifyResults(
                query,
                searchController.signal
              );
          } catch (error) {
            if (
              error.name !==
              'AbortError'
            ) {
              remoteResults = [];
            }
          }

          if (
            sequence !==
            searchSequence
          ) {
            return;
          }

          const searchablePages = [
            ...localResults,
            ...remoteResults
          ].filter((item) => {
            return [
              'Page',
              'Article',
              'Education'
            ].includes(item.type);
          });

          let contentResults = [];

          try {
            const contentGroups =
              await Promise.all(
                searchablePages
                  .slice(0, 8)
                  .map((item) =>
                    fetchRenderedContent(
                      item,
                      query,
                      searchController.signal
                    )
                  )
              );

            contentResults =
              contentGroups.flat();
          } catch (error) {
            if (
              error.name !==
              'AbortError'
            ) {
              contentResults = [];
            }
          }

          if (
            sequence !==
            searchSequence
          ) {
            return;
          }

          const combined = [
            ...localResults,
            ...remoteResults,
            ...contentResults
          ];

          const unique = [];
          const seen = new Set();

          combined.forEach((item) => {
            const key =
              item.contentMatch
                ? normalize(
                    [
                      item.url,
                      item.type,
                      item.description
                    ].join(' ')
                  )
                : normalize(
                    item.url ||
                    item.title
                  );

            if (
              !key ||
              seen.has(key)
            ) {
              return;
            }

            seen.add(key);
            unique.push(item);
          });

          unique.sort(
            (a, b) =>
              scoreResult(
                b,
                query
              ) -
              scoreResult(
                a,
                query
              )
          );

          renderResults(
            unique,
            query
          );
        };

      const closeSearch = () => {
        searchModal.classList.remove(
          'is-open'
        );

        searchModal.setAttribute(
          'aria-hidden',
          'true'
        );

        searchOpen.setAttribute(
          'aria-expanded',
          'false'
        );

        document.body.classList.remove(
          'cw-search-open'
        );
      };

      const openSearch = () => {
        header.classList.remove(
          'is-open'
        );

        menuToggle?.setAttribute(
          'aria-expanded',
          'false'
        );

        searchModal.classList.add(
          'is-open'
        );

        searchModal.setAttribute(
          'aria-hidden',
          'false'
        );

        searchOpen.setAttribute(
          'aria-expanded',
          'true'
        );

        document.body.classList.add(
          'cw-search-open'
        );

        requestAnimationFrame(
          () =>
            searchInput?.focus()
        );
      };

      searchOpen.addEventListener(
        'click',
        () => {
          if (
            searchModal
              .classList
              .contains('is-open')
          ) {
            closeSearch();
          } else {
            openSearch();
          }
        }
      );

      searchCloseButtons.forEach(
        (button) => {
          button.addEventListener(
            'click',
            closeSearch
          );
        }
      );

      searchInput?.addEventListener(
        'input',
        () => {
          window.clearTimeout(
            searchTimer
          );

          searchTimer =
            window.setTimeout(
              () => {
                runSearch(
                  searchInput.value
                );
              },
              220
            );
        }
      );

      searchForm?.addEventListener(
        'submit',
        (event) => {
          event.preventDefault();

          window.clearTimeout(
            searchTimer
          );

          runSearch(
            searchInput?.value
          );
        }
      );

      document.addEventListener(
        'keydown',
        (event) => {
          if (
            event.key === 'Escape' &&
            searchModal
              .classList
              .contains('is-open')
          ) {
            closeSearch();
            searchOpen.focus();
          }
        }
      );
    });
  }

  async function loadCart(drawer) {
    const body = drawer.querySelector('[data-cw-cart-body]');
    const foot = drawer.querySelector('[data-cw-cart-foot]');
    const total = drawer.querySelector('[data-cw-cart-total]');
    const inlineCount = drawer.querySelector('[data-cw-cart-count-inline]');
    const currency = drawer.dataset.currency || 'USD';
    if (!body) return;
    body.innerHTML = '<div class="cw-cart-loading">Loading your cart…</div>';
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await response.json();
      document.querySelectorAll('[data-cw-cart-count]').forEach((el) => { el.textContent = cart.item_count; });
      if (inlineCount) inlineCount.textContent = `(${cart.item_count})`;
      if (!cart.items.length) {
        body.innerHTML = '<div class="cw-cart-empty">Your bag is empty.<br><a href="/collections/all">Explore products →</a></div>';
        if (foot) foot.hidden = true;
        return;
      }
      body.innerHTML = cart.items.map((item) => {
        const image = item.image ? `<img src="${escapeHTML(item.image)}&width=180" alt="${escapeHTML(item.product_title)}">` : '<div></div>';
        const variant = item.variant_title && item.variant_title !== 'Default Title' ? `<small>${escapeHTML(item.variant_title)} · Qty ${item.quantity}</small>` : `<small>Qty ${item.quantity}</small>`;
        return `<article class="cw-cart-item">${image}<div class="cw-cart-item__copy"><a href="${escapeHTML(item.url)}">${escapeHTML(item.product_title)}</a>${variant}</div><div class="cw-cart-item__price">${money(item.final_line_price, currency)}</div></article>`;
      }).join('');
      if (total) total.textContent = money(cart.total_price, currency);
      if (foot) foot.hidden = false;
    } catch (error) {
      body.innerHTML = '<div class="cw-cart-empty">We could not load the cart right now. <a href="/cart">Open cart →</a></div>';
    }
  }

  function initCart(scope = document) {
    const drawer = document.querySelector('[data-cw-cart-drawer]');
    const backdrop = document.querySelector('[data-cw-cart-backdrop]');
    if (!drawer || drawer.dataset.cwReady === '1') return;
    drawer.dataset.cwReady = '1';
    const open = () => {
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      if (backdrop) backdrop.hidden = false;
      document.body.classList.add('cw-drawer-open');
      loadCart(drawer);
    };
    const close = () => {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      if (backdrop) backdrop.hidden = true;
      document.body.classList.remove('cw-drawer-open');
    };
    document.querySelectorAll('[data-cw-cart-toggle]').forEach((btn) => btn.addEventListener('click', open));
    drawer.querySelector('[data-cw-cart-close]')?.addEventListener('click', close);
    backdrop?.addEventListener('click', close);
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape') close(); });
  }

  function appendMessage(messages, text, who = 'bot') {
    const row = document.createElement('div');
    row.className = `cw-ai-message cw-ai-message--${who}`;
    row.innerHTML = `<div class="cw-ai-message__bubble">${escapeHTML(text)}</div>`;
    messages.appendChild(row);
    messages.scrollTop = messages.scrollHeight;
    return row.querySelector('.cw-ai-message__bubble');
  }

  async function searchProducts(query) {
    const params =
      new URLSearchParams({
        q: query,
        'resources[type]':
          'product',
        'resources[limit]':
          '4',
        'resources[options][unavailable_products]':
          'last'
      });

    const response =
      await fetch(
        `/search/suggest.json?${params.toString()}`,
        {
          headers: {
            Accept:
              'application/json'
          }
        }
      );

    if (!response.ok) {
      throw new Error(
        'Search failed'
      );
    }

    const data =
      await response.json();

    return (
      data?.resources
        ?.results
        ?.products || []
    );
  }

  function productCards(products) {
    if (!products.length) return '';
    return `<div class="cw-ai-products">${products.map((product) => {
      const image = product.image ? `<img src="${escapeHTML(product.image)}" alt="">` : '<span></span>';
      return `<a class="cw-ai-product" href="${escapeHTML(product.url)}">${image}<span><strong>${escapeHTML(product.title)}</strong><span>${escapeHTML(product.price || '')}</span></span><b>→</b></a>`;
    }).join('')}</div>`;
  }

  function initAI(scope = document) {
    scope.querySelectorAll('[data-cw-ai]:not([data-cw-ai-ready])').forEach((assistant) => {
      assistant.dataset.cwAiReady = '1';
      const toggle = assistant.querySelector('[data-cw-ai-toggle]');
      const close = assistant.querySelector('[data-cw-ai-close]');
      const panel = assistant.querySelector('[data-cw-ai-panel]');
      const form = assistant.querySelector('[data-cw-ai-form]');
      const input = assistant.querySelector('[data-cw-ai-input]');
      const messages = assistant.querySelector('[data-cw-ai-messages]');
      const endpoint = assistant.dataset.endpoint || '';

      const setOpen = (open) => {
        assistant.classList.toggle('is-open', open);
        toggle?.setAttribute('aria-expanded', String(open));
        panel?.setAttribute('aria-hidden', String(!open));
        if (open) requestAnimationFrame(() => input?.focus());
      };
      toggle?.addEventListener('click', () => setOpen(!assistant.classList.contains('is-open')));
      close?.addEventListener('click', () => setOpen(false));

      const send = async (message) => {
        const text = String(message || '').trim();
        if (!text || !messages) return;
        setOpen(true);
        appendMessage(messages, text, 'user');
        if (input) input.value = '';
        const bubble = appendMessage(messages, 'Looking for the best match…', 'bot');

        try {
          if (endpoint) {
            const response = await fetch(endpoint, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ message: text, shop: assistant.dataset.shop, page_url: location.href })
            });
            if (!response.ok) throw new Error('AI endpoint error');
            const data = await response.json();
            bubble.textContent = data.reply || data.message || 'I found something for you.';
            return;
          }

          const products = await searchProducts(text);
          if (products.length) {
            bubble.innerHTML = `Here are a few matches I found:${productCards(products)}`;
          } else {
            bubble.innerHTML = `I couldn’t find a direct match. <a href="/search?q=${encodeURIComponent(text)}">Search the full store →</a>`;
          }
        } catch (error) {
          bubble.innerHTML = `I couldn’t complete that request. <a href="/search?q=${encodeURIComponent(text)}">Try store search →</a>`;
        }
        messages.scrollTop = messages.scrollHeight;
      };

      form?.addEventListener('submit', (event) => {
        event.preventDefault();
        send(input?.value);
      });
      assistant.querySelectorAll('[data-cw-ai-quick]').forEach((button) => {
        button.addEventListener('click', () => {
          const action = button.dataset.cwAiAction;
          setOpen(true);
          if (action === 'browse') {
            const bubble = appendMessage(messages, 'You can browse the full collection, or tell me the product name or type you need.', 'bot');
            bubble.innerHTML += ` <a href="${escapeHTML(assistant.dataset.productsUrl || '/collections/all')}">Browse products →</a>`;
            input?.focus();
            return;
          }
          if (action === 'choose') {
            appendMessage(messages, 'Tell me what you need the product for, your preferred style, size, or budget. I’ll search the store for matching options.', 'bot');
            input?.focus();
            return;
          }
          if (action === 'order') {
            const bubble = appendMessage(messages, 'For order status and account details, open your customer account.', 'bot');
            bubble.innerHTML += ` <a href="${escapeHTML(assistant.dataset.accountUrl || '/account')}">Open account →</a>`;
            return;
          }
          send(button.dataset.cwAiQuick);
        });
      });
    });
  }

  function initV11(scope = document) {
    initHeaderFunctions(scope);
    initCart(scope);
    initAI(scope);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initV11());
  else initV11();
  document.addEventListener('shopify:section:load', (event) => initV11(event.target));
})();

/* v1.2 Clockwork-like stacked benefit slides */
(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function initStack(scope = document) {
    scope.querySelectorAll('[data-cw-slide-stack]:not([data-cw-stack-ready])').forEach((stack) => {
      stack.dataset.cwStackReady = '1';
      const slides = [...stack.querySelectorAll('[data-cw-stack-slide]')];
      if (slides.length < 2 || reducedMotion) return;

      // This stacked-slide component does not own the mini-app feature-page
      // progress meter. An older patch referenced an undefined `root` here,
      // which threw during deferred script execution and prevented every
      // initializer declared later in this file (including the Snabbb app
      // showcase) from running. Keep this hook intentionally no-op.
      const syncProgressMeter = () => {};

      let ticking = false;
      const update = () => {
        syncProgressMeter();
        ticking = false;
        if (window.matchMedia('(max-width: 989px)').matches) {
          slides.forEach((slide) => {
            slide.style.setProperty('--cw-stack-depth', '0');
            slide.classList.remove('is-covered');
          });
          return;
        }

        const stackStyle = getComputedStyle(stack);
        const baseTop = parseFloat(stackStyle.getPropertyValue('--cw-stack-top')) || 104;
        const peek = parseFloat(stackStyle.getPropertyValue('--cw-stack-peek')) || 10;
        let active = 0;

        slides.forEach((slide, index) => {
          const rect = slide.getBoundingClientRect();
          const stickyTop = baseTop + index * peek;
          if (rect.top <= stickyTop + 2) active = index;
        });

        slides.forEach((slide, index) => {
          const depth = index < active ? Math.min(active - index, 5) : 0;
          slide.style.setProperty('--cw-stack-depth', String(depth));
          slide.classList.toggle('is-covered', depth > 0);
        });
      };

      const requestUpdate = () => {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(update);
        }
      };

      update();
      window.addEventListener('scroll', requestUpdate, { passive: true });
      window.addEventListener('resize', requestUpdate, { passive: true });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initStack());
  } else {
    initStack();
  }

  document.addEventListener('shopify:section:load', (event) => initStack(event.target));
})();

/* v1.4 click-to-expand Feature + About Us navigation */
(() => {
  function initHeaderDropdowns(scope = document) {
    scope.querySelectorAll('[data-cw-header]:not([data-cw-dropdown-nav-ready])').forEach((header) => {
      header.dataset.cwDropdownNavReady = '1';
      const items = Array.from(header.querySelectorAll('[data-cw-dropdown-item]'));
      if (!items.length) return;

      const closeItem = (item, restoreFocus = false) => {
        const toggle = item.querySelector('[data-cw-dropdown-toggle]');
        if (!toggle) return;
        // Visibility is CSS-driven (hover/focus-within/.is-dropdown-open in
        // cw-shop.css) -- this only ever toggles the class, never the
        // `hidden` attribute. Writing `hidden` here used to pair with a
        // `.cw-nav__dropdown[hidden] { display: none !important; }` rule
        // that a routine "click outside the header" (anywhere on the page,
        // not just on an open dropdown) would set permanently, which then
        // blocked hover from ever opening that dropdown again for the rest
        // of the page view. See cw-header.liquid for the fuller writeup.
        item.classList.remove('is-dropdown-open');
        toggle.setAttribute('aria-expanded', 'false');
        if (restoreFocus) toggle.focus();
      };

      const closeAll = (except = null) => {
        items.forEach((item) => {
          if (item !== except) closeItem(item);
        });
      };

      items.forEach((item) => {
        const toggle = item.querySelector('[data-cw-dropdown-toggle]');
        const menu = item.querySelector('[data-cw-dropdown-menu]');
        if (!toggle || !menu) return;

        toggle.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          const shouldOpen = !item.classList.contains('is-dropdown-open');
          closeAll(item);
          if (shouldOpen) {
            item.classList.add('is-dropdown-open');
            toggle.setAttribute('aria-expanded', 'true');
          } else {
            closeItem(item);
          }
        });

        menu.addEventListener('click', (event) => event.stopPropagation());
      });

      document.addEventListener('click', (event) => {
        if (!header.contains(event.target)) closeAll();
      });

      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        const openItem = items.find((item) => item.classList.contains('is-dropdown-open'));
        if (openItem) closeItem(openItem, true);
      });

      header.querySelector('[data-cw-menu-toggle]')?.addEventListener('click', () => {
        if (!header.classList.contains('is-open')) closeAll();
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initHeaderDropdowns());
  } else {
    initHeaderDropdowns();
  }
  document.addEventListener('shopify:section:load', (event) => initHeaderDropdowns(event.target));
})();

/* =========================================================
   v1.5.4 — line draw, deterministic Snabbb chat, scroll-driven app showcase
   ========================================================= */
(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));

  function initLineDraw(scope = document) {
    scope.querySelectorAll('[data-cw-line-draw]:not([data-cw-line-ready])').forEach((section) => {
      section.dataset.cwLineReady = '1';
      if (reducedMotion) {
        section.style.setProperty('--cw-line-p1', '1');
        section.style.setProperty('--cw-line-p2', '1');
        section.style.setProperty('--cw-line-p3', '1');
        return;
      }

      let ticking = false;
      const update = () => {
        ticking = false;
        const rect = section.getBoundingClientRect();
        const vh = window.innerHeight || 1;
        const raw = (vh * .88 - rect.top) / (rect.height + vh * .42);
        const progress = clamp(raw);
        const p1 = clamp(.08 + progress * 1.55);
        const p2 = clamp(.08 + (progress - .16) * 1.75);
        const p3 = clamp(.08 + (progress - .34) * 1.9);
        section.style.setProperty('--cw-line-p1', p1.toFixed(3));
        section.style.setProperty('--cw-line-p2', p2.toFixed(3));
        section.style.setProperty('--cw-line-p3', p3.toFixed(3));
      };
      const requestUpdate = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      };
      update();
      window.addEventListener('scroll', requestUpdate, { passive: true });
      window.addEventListener('resize', requestUpdate, { passive: true });
    });
  }

  const escapeHTMLV15 = (value = '') => String(value).replace(/[&<>'"]/g, (char) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
  }[char]));

  function appendChatMessage(container, text, who) {
    if (!container) return;
    const row = document.createElement('div');
    row.className = `cw-chat-message cw-chat-message--${who}`;
    row.innerHTML = `<div class="cw-chat-message__bubble">${escapeHTMLV15(text)}</div>`;
    container.appendChild(row);
    container.scrollTop = container.scrollHeight;
  }

  function initSnabbbChat(scope = document) {
    scope.querySelectorAll('[data-cw-chat]:not([data-cw-chat-ready])').forEach((chat) => {
      chat.dataset.cwChatReady = '1';
      const toggle = chat.querySelector('[data-cw-chat-toggle]');
      const close = chat.querySelector('[data-cw-chat-close]');
      const panel = chat.querySelector('[data-cw-chat-panel]');
      const messages = chat.querySelector('[data-cw-chat-messages]');
      const form = chat.querySelector('[data-cw-chat-form]');
      const input = chat.querySelector('[data-cw-chat-input]');
      const qa = [...chat.querySelectorAll('[data-cw-chat-question]')].map((button) => ({
        button,
        question: button.dataset.question || button.textContent.trim(),
        answer: button.dataset.answer || ''
      }));

      const setOpen = (open) => {
        chat.classList.toggle('is-open', open);
        toggle?.setAttribute('aria-expanded', String(open));
        panel?.setAttribute('aria-hidden', String(!open));
      };
      toggle?.addEventListener('click', () => setOpen(!chat.classList.contains('is-open')));
      close?.addEventListener('click', () => setOpen(false));

      // WhatsApp is handled by its native link only. No chat message is generated on click.

      const answer = (item) => {
        if (!item) return;
        setOpen(true);
        appendChatMessage(messages, item.question, 'user');
        window.setTimeout(() => appendChatMessage(messages, item.answer, 'bot'), reducedMotion ? 0 : 160);
      };

      qa.forEach((item) => item.button.addEventListener('click', () => answer(item)));

      const findAnswer = (text) => {
        const t = String(text || '').toLowerCase().replace(/[–—]/g, '-');
        if (!t.trim()) return null;
        if (t.includes('inventory') || t.includes('stock') || t.includes('expiry')) return qa[1];
        if (t.includes('appointment') || t.includes('booking') || t.includes('schedule')) return qa[2];
        if (t.includes('content') || t.includes('studio') || t.includes('social')) return qa[3];
        if (t.includes('profit') || t.includes('calculator') || t.includes('revenue')) return qa[4];
        if (t.includes('todo') || t.includes('to-do') || t.includes('task')) return qa[5];
        if (t.includes('learn') || t.includes('e-learning') || t.includes('education')) return qa[6];
        if (t.includes('snabbb') || t.includes('what is')) return qa[0];
        return null;
      };

      form?.addEventListener('submit', (event) => {
        event.preventDefault();
        const text = input?.value.trim();
        if (!text) return;
        setOpen(true);
        appendChatMessage(messages, text, 'user');
        if (input) input.value = '';
        const item = findAnswer(text);
        const wantsPerson = /contact|human|person|people|team|email|whatsapp|support|staff/.test(text.toLowerCase());
        window.setTimeout(() => {
          appendChatMessage(
            messages,
            wantsPerson
              ? 'You can contact the Snabbb team directly using the Email team or WhatsApp buttons below.'
              : (item?.answer || 'I can answer questions about Snabbb, Inventory, Appointment, Content Studio, Profit Calculator, To-Do Manager and E-Learning. Choose one of the questions below.'),
            'bot'
          );
        }, reducedMotion ? 0 : 160);
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && chat.classList.contains('is-open')) setOpen(false);
      });
    });
  }

  function initAppShowcase(scope = document) {
    scope.querySelectorAll('[data-cw-app-showcase]:not([data-cw-app-ready])').forEach((showcase) => {
      showcase.dataset.cwAppReady = '1';
      const slides = [...showcase.querySelectorAll('[data-cw-app-slide]')];
      const tabs = [...showcase.querySelectorAll('[data-cw-app-tab]')];
      if (!slides.length) return;

      const scrollStep = Math.max(240, Number(showcase.dataset.scrollStep || 360));
      const stickyTop = Math.max(0, Number(showcase.dataset.stickyTop || 76));
      const minHeight = Math.max(560, parseFloat(getComputedStyle(showcase).getPropertyValue('--cw-app-min-height')) || 680);
      showcase.style.setProperty('--cw-app-scroll-step', `${scrollStep}px`);
      showcase.style.setProperty('--cw-app-count', String(slides.length));

      // One full scroll segment per mini app. The seventh/final slide also gets
      // its own complete segment before the sticky stage is allowed to release.
      const totalTravel = scrollStep * slides.length;
      showcase.style.height = `${minHeight + totalTravel}px`;
      showcase.style.minHeight = `${minHeight + totalTravel}px`;

      let active = -1;
      let ticking = false;

      const activate = (index, segmentProgress = 0) => {
        const next = Math.max(0, Math.min(slides.length - 1, index));
        active = next;

        slides.forEach((slide, i) => {
          const on = i === active;
          slide.classList.toggle('is-active', on);
          slide.setAttribute('aria-hidden', String(!on));
        });

        tabs.forEach((tab, i) => {
          tab.classList.toggle('is-complete', i < active || (i === active && segmentProgress >= .999));
          tab.classList.toggle('is-active', i === active);
          tab.setAttribute('aria-selected', String(i === active));
          const line = tab.querySelector('span');
          if (!line) return;
          let fill = 0;
          if (i < active) fill = 1;
          else if (i === active) fill = segmentProgress;
          line.style.transform = `scaleX(${Math.max(0, Math.min(1, fill)).toFixed(4)})`;
        });
      };

      const updateFromScroll = () => {
        ticking = false;

        // Use the element's actual viewport position instead of window.scrollY.
        // This also works inside Shopify's editor/preview and nested scroll areas.
        const rect = showcase.getBoundingClientRect();
        const local = Math.max(0, Math.min(totalTravel, stickyTop - rect.top));

        if (local >= totalTravel - 0.5) {
          activate(slides.length - 1, 1);
          return;
        }

        const index = Math.max(0, Math.min(slides.length - 1, Math.floor(local / scrollStep)));
        const segmentStart = index * scrollStep;
        const segmentProgress = (local - segmentStart) / scrollStep;
        activate(index, segmentProgress);
      };

      const requestUpdate = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(updateFromScroll);
      };

      // Progress bars also work as navigation controls.
      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => {
          const currentTop = showcase.getBoundingClientRect().top;
          const delta = currentTop - stickyTop + (index * scrollStep) + 2;
          window.scrollBy({ top: delta, behavior: reducedMotion ? 'auto' : 'smooth' });
        });
      });

      activate(0, 0);
      updateFromScroll();

      // Capture scroll events from window AND nested scrolling elements.
      window.addEventListener('scroll', requestUpdate, { passive: true });
      document.addEventListener('scroll', requestUpdate, { passive: true, capture: true });
      window.addEventListener('resize', requestUpdate, { passive: true });

      if ('ResizeObserver' in window) {
        const observer = new ResizeObserver(requestUpdate);
        observer.observe(showcase);
      }
    });
  }

  function initFeaturePages(scope = document) {
    scope.querySelectorAll('[data-cw-feature-page]:not([data-cw-feature-progress-ready])').forEach((root) => {
      root.dataset.cwFeatureProgressReady = '1';
      const reading = root.querySelector('[data-cw-fp-reading]');
      const panels = [...root.querySelectorAll('[data-cw-fp-panel]')];
      const steps = [...root.querySelectorAll('[data-cw-fp-step]')];
      if (!reading || !panels.length) return;

      steps.forEach((step) => {
        step.addEventListener('click', () => {
          const id = step.dataset.target;
          const target = id ? root.querySelector(`#${CSS.escape(id)}`) : null;
          if (target) target.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
        });
      });

      const lightbox = root.querySelector('[data-cw-fp-lightbox]');
      const lightboxImage = lightbox?.querySelector('[data-cw-fp-lightbox-image]');
      const lightboxCaption = lightbox?.querySelector('[data-cw-fp-lightbox-caption]');
      const lightboxClose = lightbox?.querySelector('[data-cw-fp-lightbox-close]');
      let previousBodyOverflow = '';

      const closeLightbox = () => {
        if (!lightbox || lightbox.hidden) return;
        lightbox.classList.remove('is-open');
        lightbox.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = previousBodyOverflow;
        window.setTimeout(() => {
          lightbox.hidden = true;
          if (lightboxImage) { lightboxImage.src = ''; lightboxImage.alt = ''; }
          if (lightboxCaption) lightboxCaption.textContent = '';
        }, reducedMotion ? 0 : 180);
      };

      const openLightbox = (img) => {
        if (!lightbox || !lightboxImage || !img) return;
        previousBodyOverflow = document.body.style.overflow;
        lightboxImage.src = img.currentSrc || img.src;
        lightboxImage.alt = img.alt || 'Expanded mini app screenshot';
        if (lightboxCaption) lightboxCaption.textContent = img.alt || '';
        lightbox.hidden = false;
        lightbox.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
        requestAnimationFrame(() => lightbox.classList.add('is-open'));
      };

      root.querySelectorAll('[data-cw-fp-media-expand]').forEach((button) => {
        const media = button.closest('.cw-fp-panel__media');
        const img = media?.querySelector('img');
        if (!img) { button.hidden = true; return; }
        button.addEventListener('click', () => openLightbox(img));
        img.style.cursor = 'zoom-in';
        img.addEventListener('dblclick', () => openLightbox(img));
      });

      lightboxClose?.addEventListener('click', closeLightbox);
      lightbox?.addEventListener('click', (event) => { if (event.target === lightbox) closeLightbox(); });
      document.addEventListener('keydown', (event) => { if (event.key === 'Escape' && lightbox && !lightbox.hidden) closeLightbox(); });

      let ticking = false;
      const update = () => {
        ticking = false;
        const rect = reading.getBoundingClientRect();
        const viewH = window.innerHeight || document.documentElement.clientHeight || 1;
        const start = viewH * 0.32;
        const total = Math.max(1, rect.height - viewH * 0.55);
        const travelled = Math.min(total, Math.max(0, start - rect.top));
        const pct = Math.min(100, Math.max(0, (travelled / total) * 100));
        root.style.setProperty('--cw-progress', `${pct.toFixed(2)}%`);

        const marker = viewH * 0.46;
        let active = 0;
        let best = Infinity;
        panels.forEach((panel, index) => {
          const panelRect = panel.getBoundingClientRect();
          const center = panelRect.top + Math.min(panelRect.height * .34, 260);
          const distance = Math.abs(center - marker);
          if (distance < best) {
            best = distance;
            active = index;
          }
        });
        steps.forEach((step, index) => step.classList.toggle('is-active', index === active));
      };

      const requestUpdate = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      };

      update();
      window.addEventListener('scroll', requestUpdate, { passive: true });
      window.addEventListener('resize', requestUpdate, { passive: true });
    });
  }

  function initV15(scope = document) {
    initLineDraw(scope);
    initSnabbbChat(scope);
    initAppShowcase(scope);
    initFeaturePages(scope);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initV15());
  else initV15();
  document.addEventListener('shopify:section:load', (event) => initV15(event.target));
})();
